/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { apiClient } from './client.js';

import type {
  IntakeDraftInput,
  IntakeStatus,
  IntakeSubmitInput,
  ScheduleIntakeInput,
  SignIntakeInput,
} from '@therapy/shared-validators';

/** The eight intake sections as returned by the API (each partial or null). */
export type IntakeForm = {
  readonly [K in keyof IntakeSubmitInput]: Partial<IntakeSubmitInput[K]> | null;
};

/** A client's intake assessment (parent view). */
export interface IntakeView {
  readonly clientId: string;
  readonly status: IntakeStatus;
  readonly submittedAt: string | null;
  /** True while the parent may still edit (status `form_pending`). */
  readonly canEdit: boolean;
  readonly assignedTherapistId: string | null;
  readonly scheduledSessionId: string | null;
  readonly signatureDataUrl: string | null;
  readonly signedAt: string | null;
  readonly form: IntakeForm;
}

/** Intake status of one client (admin list badge + filter). */
export interface IntakeSummary {
  readonly clientId: string;
  readonly status: IntakeStatus;
  readonly submittedAt: string | null;
}

/** The queued report job returned by generate-pdf. */
export interface IntakePdfJob {
  readonly id: string;
  readonly status: string;
}

/** Whether a generated intake PDF is available to download. */
export interface IntakeDocumentMeta {
  readonly available: boolean;
  readonly fileName: string | null;
}

const BASE = '/intake';

export const intakeApi = {
  async get(clientId: string): Promise<IntakeView> {
    const { data } = await apiClient.get<IntakeView>(`${BASE}/me/${clientId}`);
    return data;
  },
  async saveDraft(clientId: string, form: IntakeDraftInput): Promise<IntakeView> {
    const { data } = await apiClient.put<IntakeView>(`${BASE}/me/${clientId}`, form);
    return data;
  },
  async submit(clientId: string, form: IntakeSubmitInput): Promise<IntakeView> {
    const { data } = await apiClient.post<IntakeView>(`${BASE}/me/${clientId}/submit`, form);
    return data;
  },
  // ─── Admin (intake:manage) ────────────────────────────────────────────────
  async summaries(): Promise<readonly IntakeSummary[]> {
    const { data } = await apiClient.get<readonly IntakeSummary[]>(BASE);
    return data;
  },
  async review(clientId: string): Promise<IntakeView> {
    const { data } = await apiClient.get<IntakeView>(`${BASE}/${clientId}`);
    return data;
  },
  async adminUpdate(clientId: string, form: IntakeDraftInput): Promise<IntakeView> {
    const { data } = await apiClient.patch<IntakeView>(`${BASE}/${clientId}`, form);
    return data;
  },
  async schedule(clientId: string, body: ScheduleIntakeInput): Promise<IntakeView> {
    const { data } = await apiClient.post<IntakeView>(`${BASE}/${clientId}/schedule`, body);
    return data;
  },
  async sign(clientId: string, body: SignIntakeInput): Promise<IntakeView> {
    const { data } = await apiClient.post<IntakeView>(`${BASE}/${clientId}/sign`, body);
    return data;
  },
  async markArrived(clientId: string): Promise<IntakeView> {
    const { data } = await apiClient.post<IntakeView>(`${BASE}/${clientId}/arrived`);
    return data;
  },
  async generatePdf(clientId: string): Promise<IntakePdfJob> {
    const { data } = await apiClient.post<IntakePdfJob>(`${BASE}/${clientId}/generate-pdf`);
    return data;
  },
  async documentMeta(clientId: string): Promise<IntakeDocumentMeta> {
    const { data } = await apiClient.get<IntakeDocumentMeta>(`${BASE}/${clientId}/document/meta`);
    return data;
  },
  async downloadDocument(clientId: string): Promise<Blob> {
    const { data } = await apiClient.get<Blob>(`${BASE}/${clientId}/document`, {
      responseType: 'blob',
    });
    return data;
  },
};
